import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Separator } from "./ui/separator";
import { MapPin, Loader2, ArrowRight, Route as RouteIcon, ArrowDownUp, Check, Sparkles } from "lucide-react";
import { useI18n } from "../i18n";
import { geocode, getQuote } from "../lib/api";
import { useBooking } from "../lib/booking-context";
import { useCurrency } from "../lib/currency-context";
import { AnimatedNumber } from "./AnimatedNumber";

function AddressInput({ label, placeholder, value, onChange, onSelect, testid }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleChange = (v) => {
    onChange(v);
    onSelect(null);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 3) { setItems([]); setOpen(false); return; }
    setLoading(true); setOpen(true);
    timer.current = setTimeout(async () => {
      try { setItems(await geocode(v.trim())); } catch { setItems([]); } finally { setLoading(false); }
    }, 550);
  };

  return (
    <div className="relative" ref={boxRef}>
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <div className="relative mt-1.5">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-accent" />
        <Input value={value} onChange={(e) => handleChange(e.target.value)} onFocus={() => items.length && setOpen(true)} placeholder={placeholder} className="pl-9 rounded-xl h-12 focus-gold bg-background/50" data-testid={testid} autoComplete="off" />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {open && (loading || items.length > 0 || value.trim().length >= 3) && (
        <div className="autocomplete-list absolute z-30 mt-1 w-full rounded-xl border border-border glass-strong shadow-xl" data-testid={`${testid}-suggestions`}>
          {loading && <div className="px-3 py-2.5 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {t("calc.searching")}</div>}
          {!loading && items.length === 0 && <div className="px-3 py-2.5 text-sm text-muted-foreground">{t("calc.noResult")}</div>}
          {!loading && items.map((it, i) => (
            <button key={`${it.label}-${i}`} type="button" className="block w-full px-3 py-2.5 text-left text-sm hover:bg-secondary/60 transition-colors border-b border-border/60 last:border-0" onClick={() => { onChange(it.label); onSelect(it); setOpen(false); }} data-testid={`${testid}-option-${i}`}>
              <span className="line-clamp-2">{it.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const Calculator = ({ config }) => {
  const { t } = useI18n();
  const { requestBooking } = useBooking();
  const { fmt, convert, symbol, currency } = useCurrency();
  const rateDec = currency === "EUR" ? 2 : 0;
  const [fromText, setFromText] = useState("");
  const [toText, setToText] = useState("");
  const [fromSel, setFromSel] = useState(null);
  const [toSel, setToSel] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const rate = config?.rate_czk_per_km || 23;
  const oldRate = config?.old_rate_czk_per_km || 25;

  const swap = () => {
    setFromText(toText); setToText(fromText);
    setFromSel(toSel); setToSel(fromSel);
  };

  const calculate = useCallback(async () => {
    setError(""); setResult(null);
    if (!fromSel || !toSel) { setError(t("calc.selectHint")); return; }
    setBusy(true);
    try {
      const q = await getQuote({ lat: fromSel.lat, lon: fromSel.lon }, { lat: toSel.lat, lon: toSel.lon }, fromText, toText);
      setResult(q);
    } catch { setError(t("calc.error")); } finally { setBusy(false); }
  }, [fromSel, toSel, fromText, toText, t]);

  const inclusions = [t("hero.badge2"), t("hero.badge3"), t("benefits.items")[4]?.t].filter(Boolean);

  return (
    <section id="calculator" className="relative py-14 sm:py-18 lg:py-24 overflow-hidden" data-testid="calculator-section">
      <div className="mesh-glow top-1/2 left-[-12rem]" />
      <div className="relative max-w-6xl xl:max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <span className="eyebrow inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> {t("calc.eyebrow")}</span>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.01em]">{t("calc.title")}</h2>
          <p className="mt-3 text-muted-foreground">{t("calc.subtitle")}</p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
          {/* Inputs */}
          <div className="lg:col-span-7 rounded-[18px] border border-border glass edge-light p-5 sm:p-7">
            <div className="relative grid grid-cols-1 gap-4">
              <AddressInput label={t("calc.from")} placeholder={t("calc.fromPh")} value={fromText} onChange={setFromText} onSelect={setFromSel} testid="calc-from-input" />
              <div className="flex justify-center -my-1">
                <button type="button" onClick={swap} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card hover:border-accent/60 hover:text-accent transition-colors" data-testid="calc-swap-button" aria-label="Swap">
                  <ArrowDownUp className="h-4 w-4" />
                </button>
              </div>
              <AddressInput label={t("calc.to")} placeholder={t("calc.toPh")} value={toText} onChange={setToText} onSelect={setToSel} testid="calc-to-input" />
            </div>
            <Button onClick={calculate} disabled={busy} className="mt-6 w-full rounded-xl shimmer active:scale-[0.98] h-12" data-testid="calc-calculate-button">
              {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("calc.calculating")}</> : <>{t("calc.calcBtn")} <ArrowRight className="ml-1 h-4 w-4" /></>}
            </Button>
            {error && <p className="mt-3 text-sm text-destructive" data-testid="calc-error">{error}</p>}
          </div>

          {/* Quote summary */}
          <div className="lg:col-span-5 rounded-[18px] border border-border bg-background/50 p-5 sm:p-7 flex flex-col">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("calc.price")}</span>
              <span className="inline-flex items-center rounded-full bg-accent/15 text-[hsl(var(--gold-deep))] px-3 py-1 text-xs font-semibold tabular-nums">
                {oldRate > rate && <span className="line-through mr-1.5 opacity-60">{fmt(oldRate, rateDec)}</span>}{fmt(rate, rateDec)} / km
              </span>
            </div>

            <div className="mt-2 min-h-[64px] flex items-end">
              <AnimatePresence mode="wait">
                {result ? (
                  <motion.div key="price" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.35 }} data-testid="calc-result">
                    <div className="font-display text-4xl sm:text-5xl font-semibold gold-text tabular-nums">
                      <AnimatedNumber value={convert(result.price_czk)} data-testid="calc-price" /> <span className="text-xl text-muted-foreground font-normal">{symbol}</span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground tabular-nums">
                      <RouteIcon className="inline h-3.5 w-3.5 mr-1 text-accent" /><span data-testid="calc-distance">{result.distance_km}</span> km · {fmt(result.rate_czk_per_km, rateDec)}/km
                    </p>
                    {(result.pickup_fee_czk || result.surcharge_czk) ? (
                      <div className="mt-3 space-y-1 text-sm tabular-nums" data-testid="calc-breakdown">
                        <div className="flex items-center justify-between text-muted-foreground">
                          <span>{t("calc.base")}</span><span>{fmt(result.base_price_czk)}</span>
                        </div>
                        {result.surcharge_czk ? (
                          <div className="flex items-center justify-between">
                            <span className="text-[hsl(var(--lime))]">+ {t("calc.ldFee")}</span>
                            <span className="text-[hsl(var(--lime))] font-semibold">+ {fmt(result.surcharge_czk)}</span>
                          </div>
                        ) : null}
                        {result.pickup_fee_czk ? (
                          <div className="flex items-center justify-between text-muted-foreground">
                            <span>+ {t("calc.pickup")}</span><span>+ {fmt(result.pickup_fee_czk)}</span>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </motion.div>
                ) : (
                  <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="font-display text-4xl sm:text-5xl font-semibold text-muted-foreground/40 tabular-nums">
                    — <span className="text-xl font-normal">{symbol}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <Separator className="my-5 bg-border" />
            <ul className="space-y-2.5">
              {inclusions.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-foreground/80">
                  <Check className="h-4 w-4 text-[hsl(var(--success))]" /> {f}
                </li>
              ))}
            </ul>

            <Button
              className="mt-6 w-full rounded-xl active:scale-[0.98] h-12"
              variant={result ? "default" : "outline"}
              disabled={!result}
              onClick={() => result && requestBooking({ from_location: fromText, to_location: toText, category: "long_distance", route: `${fromText} → ${toText}`, estimated_price: result.price_czk, distance_km: result.distance_km })}
              data-testid="calc-book-button"
            >
              {t("calc.bookThis")}
            </Button>
            <p className="mt-3 text-xs text-muted-foreground text-center">{t("calc.note")}</p>
          </div>
        </div>
      </div>
    </section>
  );
};
